import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, 
  Download, 
  Trash2, 
  CheckCircle, 
  Clock, 
  Mail,
  Users,
  UserCheck,
  UserX,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (value) => {
  if (!value) return '-';
  return String(value).replace('T', ' ').replace('Z', '').slice(0, 16);
};

const AdminNewsletter = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'confirmed', 'pending'

  const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;

  const fetchSubscribers = React.useCallback(async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/admin/newsletter`, { headers });
      setSubscribers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Fehler beim Laden',
        description: 'Die Abonnenten konnten nicht geladen werden.',
      });
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, toast]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const filtered = useMemo(() => {
    let data = subscribers;

    // Filter by status
    if (filter === 'confirmed') {
      data = data.filter((s) => s.is_confirmed);
    } else if (filter === 'pending') {
      data = data.filter((s) => !s.is_confirmed);
    }

    // Filter by search query
    if (query.trim()) {
      const q = query.toLowerCase();
      data = data.filter((s) => s.email.toLowerCase().includes(q));
    }

    return data;
  }, [subscribers, filter, query]);

  const stats = useMemo(() => {
    const total = subscribers.length;
    const confirmed = subscribers.filter((s) => s.is_confirmed).length;
    const pending = total - confirmed;
    return { total, confirmed, pending };
  }, [subscribers]);

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Möchtest du "${email}" wirklich löschen?`)) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/admin/newsletter/${id}`, { headers });
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      toast({
        title: 'Gelöscht',
        description: `${email} wurde entfernt.`,
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Der Abonnent konnte nicht gelöscht werden.',
      });
    }
  };

  const handleConfirm = async (id, email) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/newsletter/${id}/confirm`, {}, { headers });
      setSubscribers((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, is_confirmed: true, confirmed_at: new Date().toISOString() } : s
        )
      );
      toast({
        title: 'Bestätigt',
        description: `${email} wurde manuell bestätigt.`,
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Der Abonnent konnte nicht bestätigt werden.',
      });
    }
  };

  const handleExport = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/admin/newsletter/export`, { headers });
      
      const data = response.data;
      if (!data.length) {
        toast({
          title: 'Keine Daten',
          description: 'Es gibt keine bestätigten Abonnenten zum Exportieren.',
        });
        return;
      }

      // Create CSV
      const csv = [
        'E-Mail,Angemeldet am,Bestätigt am',
        ...data.map((s) => `${s.email},${formatDate(s.created_at)},${formatDate(s.confirmed_at)}`)
      ].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `firmbay-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      toast({
        title: 'Export erfolgreich',
        description: `${data.length} bestätigte Abonnenten exportiert.`,
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Der Export ist fehlgeschlagen.',
      });
    }
  };

  return (
    <div className="min-h-screen pt-40 pb-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="icon">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Newsletter-Abonnenten</h1>
              <p className="text-sm text-gray-600">Verwalte alle Newsletter-Anmeldungen</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchSubscribers} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700 text-white">
              <Download className="w-4 h-4 mr-2" />
              CSV Export
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card 
            className={`cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setFilter('all')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Gesamt</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all ${filter === 'confirmed' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setFilter('confirmed')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Bestätigt</p>
                  <p className="text-3xl font-bold text-green-600">{stats.confirmed}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-orange-500' : ''}`}
            onClick={() => setFilter('pending')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ausstehend</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <UserX className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscriber Table */}
        <Card className="border-2 border-gray-200 shadow-sm">
          <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <CardTitle className="text-xl flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Abonnenten
              {filter !== 'all' && (
                <Badge variant={filter === 'confirmed' ? 'default' : 'secondary'}>
                  {filter === 'confirmed' ? 'Bestätigt' : 'Ausstehend'}
                </Badge>
              )}
            </CardTitle>
            <div className="w-full md:w-96">
              <Input 
                value={query} 
                onChange={(e) => setQuery(e.target.value)} 
                placeholder="E-Mail suchen…" 
                data-testid="newsletter-admin-search"
              />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-gray-600">Lade Daten…</div>
            ) : (
              <>
                <div className="text-sm text-gray-600 mb-4">
                  Einträge: <span className="font-semibold text-gray-900">{filtered.length}</span>
                </div>

                <div className="overflow-auto border border-gray-200 rounded-lg bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr>
                        <th className="text-left font-semibold px-3 py-2">Status</th>
                        <th className="text-left font-semibold px-3 py-2">E-Mail</th>
                        <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">Angemeldet</th>
                        <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">Bestätigt</th>
                        <th className="text-right font-semibold px-3 py-2">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((subscriber) => (
                        <tr key={subscriber.id} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="px-3 py-2">
                            {subscriber.is_confirmed ? (
                              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Bestätigt
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                                <Clock className="w-3 h-3 mr-1" />
                                Ausstehend
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-900">{subscriber.email}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {formatDate(subscriber.created_at)}
                          </td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {subscriber.confirmed_at ? formatDate(subscriber.confirmed_at) : '-'}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              {!subscriber.is_confirmed && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleConfirm(subscriber.id, subscriber.email)}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  data-testid={`confirm-${subscriber.id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(subscriber.id, subscriber.email)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-${subscriber.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}

                      {filtered.length === 0 && (
                        <tr>
                          <td className="px-3 py-6 text-center text-gray-500" colSpan={5}>
                            Keine Abonnenten gefunden.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminNewsletter;
